import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';

export default function Solution() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [currentFeatureSlide, setCurrentFeatureSlide] = useState(0);
  const [isFeatureAutoPlaying, setIsFeatureAutoPlaying] = useState(true);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  
  // Parallax effects
  const parallax1 = useParallax({ speed: 0.3, direction: 'up' });
  const parallax2 = useParallax({ speed: 0.5, direction: 'down' });
  const parallax3 = useParallax({ speed: 0.4, direction: 'up' });

  const scrollToContact = () => {
    const element = document.getElementById('contato');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const detailedSolutions = [
    {
      title: 'Controle de Ponto Inteligente',
      subtitle: 'Tecnologia que funciona onde você precisa',
      icon: 'ri-smartphone-line',
      color: 'from-emerald-500 to-teal-500',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/e8371b408948ede68e6d06011451a92f.png',
      features: [
        {
          icon: 'ri-fingerprint-line',
          title: 'Reconhecimento Facial Avançado',
          description: 'Tecnologia de IA que garante autenticidade em qualquer condição de luz'
        },
        {
          icon: 'ri-map-pin-user-line',
          title: 'GPS de Alta Precisão',
          description: 'Localização exata com raio configurável para cada área de trabalho'
        },
        {
          icon: 'ri-wifi-off-line',
          title: 'Modo Offline Completo',
          description: 'Funciona sem internet e sincroniza automaticamente quando conectar'
        }
      ]
    },
    {
      title: 'Painel Gerencial Completo',
      subtitle: 'Visão total da sua operação em tempo real',
      icon: 'ri-dashboard-line',
      color: 'from-cyan-500 to-blue-500',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/369b0ed44514836fa0df719220426fad.jpeg',
      features: [
        {
          icon: 'ri-line-chart-line',
          title: 'Relatórios em Tempo Real',
          description: 'Acompanhe presença, horas trabalhadas e produtividade instantaneamente'
        },
        {
          icon: 'ri-notification-3-line',
          title: 'Alertas Inteligentes',
          description: 'Notificações automáticas de atrasos, faltas e horas extras'
        },
        {
          icon: 'ri-file-chart-line',
          title: 'Análises Avançadas',
          description: 'Insights sobre padrões de trabalho e oportunidades de otimização'
        }
      ]
    },
    {
      title: 'Gestão de Jornada Automatizada',
      subtitle: 'Elimine erros e economize tempo',
      icon: 'ri-time-line',
      color: 'from-indigo-500 to-purple-500',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/09bcb44307ed37acbcee4ee53b9005ae.jpeg',
      features: [
        {
          icon: 'ri-calculator-line',
          title: 'Cálculo Automático',
          description: 'Horas normais, extras, noturnas e banco de horas calculados automaticamente'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Conformidade Legal',
          description: 'Sempre atualizado com a legislação trabalhista brasileira'
        },
        {
          icon: 'ri-file-download-line',
          title: 'Exportação Simplificada',
          description: 'Dados prontos para sua contabilidade em múltiplos formatos'
        }
      ]
    },
    {
      title: 'Aplicativo Mobile Intuitivo',
      subtitle: 'Simplicidade para toda a equipe',
      icon: 'ri-phone-line',
      color: 'from-pink-500 to-rose-500',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/e8a7a81b7b7c176736a5784155c68d0d.png',
      features: [
        {
          icon: 'ri-user-smile-line',
          title: 'Interface Amigável',
          description: 'Design simples que qualquer pessoa aprende em minutos'
        },
        {
          icon: 'ri-history-line',
          title: 'Histórico Transparente',
          description: 'Colaboradores visualizam seus próprios registros e horas'
        },
        {
          icon: 'ri-message-3-line',
          title: 'Comunicação Direta',
          description: 'Canal de comunicação entre equipe e gestão integrado'
        }
      ]
    }
  ];

  // Auto-play do carrossel de soluções
  useEffect(() => {
    if (!isFeatureAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentFeatureSlide((prev) => (prev + 1) % detailedSolutions.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isFeatureAutoPlaying, detailedSolutions.length]);

  const nextFeatureSlide = () => {
    setIsFeatureAutoPlaying(false);
    setCurrentFeatureSlide((prev) => (prev + 1) % detailedSolutions.length);
  };

  const prevFeatureSlide = () => {
    setIsFeatureAutoPlaying(false);
    setCurrentFeatureSlide((prev) => (prev - 1 + detailedSolutions.length) % detailedSolutions.length);
  };

  const goToFeatureSlide = (index: number) => {
    setIsFeatureAutoPlaying(false);
    setCurrentFeatureSlide(index);
  };

  const testimonials = [
    {
      name: 'João Silva',
      role: 'Produtor Rural - Fazenda Santa Maria',
      image: 'https://public.readdy.ai/ai/img_res/5ed41dce4017841a2b886da42e6c1668.jpg',
      quote: 'Reduzi 30% dos custos com folha de pagamento no primeiro mês. O sistema é simples e funciona mesmo sem internet.',
      rating: 5
    },
    {
      name: 'Maria Santos',
      role: 'Gestora de RH - Agropecuária Vale Verde',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20confident%20Brazilian%20female%20HR%20manager%20in%20agricultural%20office%20setting%20wearing%20business%20casual%20attire%20with%20farming%20background%20natural%20lighting%20professional%20photography&width=400&height=400&seq=testimonial002&orientation=squarish',
      quote: 'Acabaram as discussões sobre horas extras. Tudo fica registrado e transparente para todos.',
      rating: 5
    },
    {
      name: 'Carlos Mendes',
      role: 'Administrador - Fazenda Boa Esperança',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20confident%20Brazilian%20farm%20administrator%20in%20rural%20setting%20wearing%20work%20shirt%20with%20tablet%20showing%20modern%20agricultural%20management%20natural%20lighting%20documentary%20photography&width=400&height=400&seq=testimonial003&orientation=squarish',
      quote: 'O fechamento da folha que levava dias agora leva horas. Economia de tempo e dinheiro.',
      rating: 5
    }
  ];

  // Auto-play do carrossel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  const features = [
    {
      icon: 'ri-smartphone-line',
      title: 'App móvel intuitivo',
      description: 'Interface simples que qualquer colaborador consegue usar',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: 'ri-wifi-off-line',
      title: 'Funciona offline',
      description: 'Não depende de internet para registrar o ponto',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      icon: 'ri-map-pin-user-line',
      title: 'Geolocalização precisa',
      description: 'Confirma que o colaborador está no local de trabalho',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: 'ri-fingerprint-line',
      title: 'Reconhecimento facial',
      description: 'Impede marcações por terceiros',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Painel gerencial completo',
      description: 'Visualize tudo em tempo real de qualquer lugar',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: 'ri-file-download-line',
      title: 'Relatórios automáticos',
      description: 'Exportação pronta para sua contabilidade',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <section 
      ref={ref}
      id="solucao" 
      className={`py-20 lg:py-28 bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50 relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      {/* Elementos decorativos de fundo com parallax */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${parallax1}px)` }}
        ></div>
        <div 
          className="absolute top-1/3 right-20 w-96 h-96 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '1s', transform: `translateY(${parallax2}px)` }}
        ></div>
        <div 
          className="absolute bottom-20 left-1/4 w-80 h-80 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '2s', transform: `translateY(${parallax3}px)` }}
        ></div>
      </div>

      {/* Padrão de grade tecnológico */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#14b8a6 1px, transparent 1px), linear-gradient(90deg, #14b8a6 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 animate-bounce">
            <span className="relative bg-gradient-to-r from-teal-600 via-cyan-500 to-emerald-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-2xl inline-flex items-center gap-2 transform hover:scale-110 transition-all duration-300 cursor-pointer group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-cyan-500 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute inset-0 animate-pulse bg-white/20 rounded-full"></div>
              <i className="ri-lightbulb-flash-line text-lg relative z-10 group-hover:rotate-12 transition-transform duration-300"></i>
              <span className="relative z-10">Nossa Solução</span>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"></div>
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Tecnologia desenvolvida para o campo
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Sistema completo de controle de ponto que realmente funciona nas condições do agronegócio brasileiro.
          </p>
        </div>

        {/* Carrossel Detalhado de Soluções */}
        <div className="mb-20">
          <div className="relative max-w-6xl mx-auto">

            {/* Abas de navegação */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {detailedSolutions.map((solution, index) => (
                <button
                  key={index}
                  onClick={() => goToFeatureSlide(index)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    index === currentFeatureSlide
                      ? `bg-gradient-to-r ${solution.color} text-white shadow-lg scale-105`
                      : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-teal-300 hover:text-teal-600'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${solution.icon} text-base`}></i>
                  </div>
                  <span className="hidden sm:inline">{index === 0 ? 'Controle Inteligente' : index === 2 ? 'Gestão Automatizada' : solution.title.split(' ').slice(0, 2).join(' ')}</span>
                </button>
              ))}
            </div>

            {/* Painel principal */}
            <div className="bg-white rounded-3xl shadow-2xl border border-teal-100 overflow-hidden">
              {detailedSolutions.map((solution, index) => (
                <div
                  key={index}
                  className={`transition-all duration-500 ${
                    currentFeatureSlide === index
                      ? 'opacity-100 block'
                      : 'opacity-0 hidden'
                  }`}
                >
                  <div className="grid md:grid-cols-2 gap-0">

                    {/* Coluna esquerda — imagem + título */}
                    <div className="relative h-64 md:h-auto min-h-[320px] overflow-hidden">
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-br ${solution.color} opacity-70`}></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                      <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-xl border border-white/30`}>
                            <i className={`${solution.icon} text-2xl text-white`}></i>
                          </div>
                          <span className="text-sm font-medium text-white/80">{solution.subtitle}</span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold leading-tight">{solution.title}</h3>
                      </div>
                    </div>

                    {/* Coluna direita — lista de pontos */}
                    <div className="p-8 flex flex-col justify-center gap-6 bg-gradient-to-br from-white to-gray-50">
                      <p className="text-xs font-bold uppercase tracking-widest text-teal-500 mb-2">Principais recursos</p>
                      {solution.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-4 group">
                          <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br ${solution.color} rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                            <i className={`${feature.icon} text-xl text-white`}></i>
                          </div>
                          <div>
                            <h4 className="text-base font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                              {feature.title}
                            </h4>
                            <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
                          </div>
                        </div>
                      ))}

                      {/* Botão CTA interno */}
                      <button
                        onClick={scrollToContact}
                        className={`mt-2 self-start flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r ${solution.color} shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap`}
                      >
                        Quero conhecer
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-sm"></i>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Setas de navegação */}
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={prevFeatureSlide}
                className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md border border-gray-200 hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl text-teal-600 hover:text-white"></i>
              </button>
              <div className="flex gap-2">
                {detailedSolutions.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToFeatureSlide(index)}
                    className={`transition-all duration-300 rounded-full cursor-pointer ${
                      index === currentFeatureSlide
                        ? 'w-8 h-3 bg-teal-500'
                        : 'w-3 h-3 bg-gray-300 hover:bg-teal-300'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={nextFeatureSlide}
                className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md border border-gray-200 hover:bg-teal-600 hover:border-teal-600 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl text-teal-600"></i>
              </button>
            </div>

          </div>
        </div>

        {/* CTA Final */}
        <div className="text-center bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-3xl p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto para transformar sua gestão?
            </h3>
            <p className="text-xl text-white/95 mb-8 max-w-2xl mx-auto">
              Agende uma demonstração gratuita e veja como funciona na prática
            </p>
            <div className="flex justify-center px-4">
              <button
                onClick={scrollToContact}
                className="group bg-white text-teal-600 px-6 sm:px-10 py-4 rounded-lg text-sm sm:text-base md:text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl w-full sm:w-auto max-w-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  Agendar demonstração gratuita
                  <i className="ri-calendar-check-line text-lg sm:text-xl group-hover:scale-110 transition-transform"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}